import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDeleteQuery, usePatchQuery } from "../hooks";
import { useUserContext } from "../contexts";
import Button from "./Button";
import Panel from "./Panel";
import { ADMIN, MEMBER } from "../constants";
import { Pencil } from "lucide-react";
import { Input } from "./Form";
import ConfirmationModal from "./ConfirmationModal";
import DeleteButton from "./DeleteButton";

function EditableItem({ as = "div", label, value, field, path, identifier }) {
  const [editing, setEditing] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const [error, setError] = useState(null);
  const { mutateAsync, isLoading } = usePatchQuery(path, {
    identifier,
  });

  function handleEditClick() {
    setEditing(true);
    setInputValue(value);
    setError(null);
  }

  async function handleSave() {
    try {
      await mutateAsync({ [field]: inputValue });
      setEditing(false);
    } catch (e) {
      setError("Failed to update. Please try again.");
    }
  }

  function handleCancel() {
    setEditing(false);
    setInputValue(value);
    setError(null);
  }

  const Component = as;

  return (
    <Component className="flex items-center gap-2">
      <span className="font-bold text-gray-500 py-2">{label} : </span>
      {editing ? (
        <>
          <Input
            type="text"
            id={field}
            name={field}
            className="border rounded px-2 py-1 text-sm w-[8rem]"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            disabled={isLoading}
            autoFocus
          />
          <Button
            className="text-green-600 px-2"
            onClick={handleSave}
            disabled={isLoading}
            loading={isLoading}
            title="Save"
            px="px-2"
            py="py-1"
            backgroundColor="bg-transparent"
            textColor="text-green-600"
            hover="hover:bg-green-50"
          >
            ✓
          </Button>
          <Button
            className="text-gray-500 px-2"
            onClick={handleCancel}
            disabled={isLoading}
            title="Cancel"
            px="px-2"
            py="py-1"
            backgroundColor="bg-transparent"
            textColor="text-gray-500"
            hover="hover:bg-gray-100"
          >
            ✗
          </Button>
          {error && <span className="text-red-500 text-xs ml-2">{error}</span>}
        </>
      ) : (
        <>
          <span>{value}</span>
          <Button
            className="ml-2 text-gray-400 hover:text-gray-700"
            onClick={handleEditClick}
            title="Edit"
            aria-label={`Edit ${label}`}
            px="px-1"
            py="py-1"
            backgroundColor="bg-transparent"
            textColor="text-gray-400"
            hover="hover:bg-gray-200"
          >
            <Pencil className="w-4 h-4" />
          </Button>
        </>
      )}
    </Component>
  );
}

function Item({ label, children }) {
  return (
    <li className="py-2">
      <span className="font-bold text-gray-500">{label} : </span>
      {children}
    </li>
  );
}

export default function Profile() {
  const navigate = useNavigate();

  const { user } = useUserContext();

  const deleteUser = useDeleteQuery(
    user.profileType === ADMIN ? "users/admins" : "users/members"
  );

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  async function handleDeleteUser() {
    try {
      await deleteUser.mutateAsync(user.id);
      navigate("/login");
    } catch (error) {
      console.error("Failed to delete user:", error);
      alert("Failed to delete user. Please try again.");
    }
  }

  return (
    <>
      <Panel left="Profile">
        <ul>
          <Item label="Family">{user.family.name}</Item>
          <EditableItem
            as="li"
            label="Name"
            value={user.name}
            field="name"
            path={user.profileType === ADMIN ? "users/admins" : "users/members"}
            identifier={user.id}
          />
          <EditableItem
            as="li"
            label="Email"
            value={user.email}
            field="email"
            path={user.profileType === ADMIN ? "users/admins" : "users/members"}
            identifier={user.id}
          />
          {user.profileType === MEMBER && (
            <EditableItem
              as="li"
              label="Responsibility Reward"
              value={user.responsibilityReward}
              field="responsibilityReward"
              path="users/members"
              identifier={user.id}
            />
          )}
        </ul>
        <div className="mt-4">
          <DeleteButton
            onClick={() => setIsDeleteModalOpen(true)}
            loading={deleteUser.isLoading}
            disabled={deleteUser.isLoading}
          >
            Delete Account
          </DeleteButton>
        </div>
      </Panel>
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteUser}
        heading="Confirm Deletion"
        message="Are you sure you want to delete your account? This action cannot be undone."
        confirmLabel="Delete"
        loading={deleteUser.isLoading}
      />
    </>
  );
}
